import React from 'react';
import { buildSimplePrintDocument } from './simple-document';
import { getCompanyLetterhead } from '../company-letterhead';
import type { PrintColumn, PrintDocumentModel } from '../types';
import type { SalesInvoice, SalesInvoiceItem } from '@/services/salesInvoice.service';
import type { CompanyPrintInfo } from '@/components/sales-invoice/PrintableSalesInvoice';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import { formatPrintAmount } from '@/constants/uaDirhamDisplay';
import { formatAmountInWordsPlain } from '@/utils/amountInWords';
import dayjs from 'dayjs';

const MAX_PARTY_BLOCK_DNS = 2;
const DELIVERY_NOTE_GRID_COLUMNS = 5;

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Hide machine linkage blobs that used to be dumped into SI notes / line notes. */
function formatInvoiceNotesForPrint(raw: unknown): string {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  if (text.startsWith('{') && text.includes('"source"')) {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (parsed.source === 'epc_certification') {
        const certNo = String(parsed.certification_number || '').trim();
        const parts = [
          certNo
            ? `Progress billing from payment certification ${certNo}.`
            : 'Progress billing from EPC payment certification.',
          'VAT as shown on this invoice.',
        ];
        return parts.join(' ');
      }
    } catch {
      /* fall through */
    }
  }
  return text;
}

function isMachineLineNote(raw: unknown): boolean {
  const text = String(raw ?? '').trim();
  if (!text) return true;
  return /^certification_id=/i.test(text) || /^[0-9a-f-]{36}$/i.test(text);
}

function resolveCustomerTrn(invoice: SalesInvoice): string {
  const snap = String((invoice as any).customer_trn ?? '').trim();
  if (snap) return snap.replace(/^TRN:\s*/i, '');
  const customer = (invoice as any).customer;
  const fromMaster = String(customer?.tax_registration_number ?? customer?.vat_number ?? '').trim();
  return fromMaster.replace(/^TRN:\s*/i, '');
}

export function buildSalesInvoicePrintDocument(
  invoice: SalesInvoice,
  items: SalesInvoiceItem[],
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  opts?: {
    letterhead?: boolean;
    revenueServiceTypeLabel?: string;
    approvalSigners?: ApprovalPrintSigner[];
  }
): PrintDocumentModel {
  const useLh = opts?.letterhead !== false;
  const currency = invoice.currency || 'AED';
  const customerTrn = resolveCustomerTrn(invoice);
  const companyTrn = (companyInfo.taxNumber || '').replace(/^TRN:\s*/i, '');
  const inv = invoice as SalesInvoice & Record<string, unknown>;

  const customerPo =
    String(invoice.customer_po_number ?? '').trim() ||
    String((invoice.salesOrder as { customer_po_number?: string } | undefined)?.customer_po_number ?? '').trim();
  const customerPoDateRaw =
    invoice.customer_po_date ??
    (invoice.salesOrder as { customer_po_date?: string } | undefined)?.customer_po_date ??
    null;
  const customerPoDate = customerPoDateRaw
    ? dayjs(customerPoDateRaw).isValid()
      ? dayjs(customerPoDateRaw).format('DD-MM-YYYY')
      : String(customerPoDateRaw).trim()
    : '';

  const leftRows = [
    { label: 'M/S', value: invoice.customer_name || '' },
    ...(customerTrn ? [{ label: 'Customer TRN', value: customerTrn }] : []),
    ...(customerPo ? [{ label: 'Customer PO', value: customerPo }] : []),
    ...(customerPoDate ? [{ label: 'PO Date', value: customerPoDate }] : []),
    { label: 'Currency', value: currency },
  ].filter((r) => r.value);

  const deliveryNoteList = (() => {
    const fromList = (invoice.delivery_notes || [])
      .map((dn) => String(dn.delivery_note_number ?? '').trim())
      .filter(Boolean);
    if (fromList.length) return [...new Set(fromList)];
    const fromArray = (invoice.delivery_note_numbers || [])
      .map((n) => String(n ?? '').trim())
      .filter(Boolean);
    if (fromArray.length) return [...new Set(fromArray)];
    const single = String(invoice.delivery_note_number ?? '').trim();
    return single ? [single] : [];
  })();
  // The party-block value column is narrow; longer lists get a full-width block above the items.
  const deliveryNotesBlock = deliveryNoteList.length > MAX_PARTY_BLOCK_DNS;
  const deliveryNoteNumbers = deliveryNotesBlock
    ? `${deliveryNoteList.length} delivery notes (listed below)`
    : deliveryNoteList.join(', ');

  const rightRows = [
    ...(companyTrn ? [{ label: 'Company TRN', value: companyTrn }] : []),
    { label: 'Invoice No.', value: invoice.invoice_number || '' },
    {
      label: 'Date',
      value: invoice.invoice_date ? dayjs(invoice.invoice_date).format('DD-MM-YYYY') : '',
    },
    ...(deliveryNoteNumbers ? [{ label: 'DN No.', value: deliveryNoteNumbers }] : []),
  ].filter((r) => r.value);

  const sortedItems = [...items].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));
  const hasAnyItemCode = sortedItems.some((line) => String(line.item_code ?? '').trim().length > 0);

  const columns: PrintColumn[] = [
    { key: 'sno', label: 'S.No', width: '32px' },
    ...(hasAnyItemCode ? [{ key: 'code', label: 'Code', width: '64px' } as PrintColumn] : []),
    { key: 'description', label: 'Description' },
    { key: 'qty', label: 'Qty', align: 'right', width: '40px' },
    { key: 'uom', label: 'UOM', width: '36px' },
    { key: 'unit', label: 'Unit Price', align: 'right', width: '88px' },
    { key: 'vatPct', label: 'VAT %', align: 'right', width: '42px' },
    { key: 'vatAmt', label: 'VAT Amt', align: 'right', width: '108px' },
    { key: 'total', label: 'Total', align: 'right', width: '116px' },
  ];

  const lines = sortedItems.map((line, idx) => {
      const qty = toNum(line.quantity);
      const price = toNum(line.unit_price);
      const gross = toNum(line.net_amount ?? line.line_total ?? qty * price);
      const vatAmt = toNum(line.vat_amount);
      const vatPct = vatAmt > 0 ? toNum(line.vat_percentage) || 5 : 0;
      const total = gross + vatAmt;
      const extraLines = [line.notes].filter((n) => n && !isMachineLineNote(n)) as string[];
      const qtyDisplay = Number.isInteger(qty) ? String(qty) : String(qty);
      const itemCode = String(line.item_code ?? '').trim();
      return {
        lineNumber: line.line_number ?? idx + 1,
        description: line.item_description || itemCode || '—',
        cells: {
          ...(hasAnyItemCode ? { code: itemCode } : {}),
          qty: qtyDisplay,
          uom: line.uom || '—',
          unit: formatPrintAmount(price, currency),
          vatPct: String(vatPct),
          vatAmt: formatPrintAmount(vatAmt, currency),
          total: formatPrintAmount(total, currency),
        },
        extraLines,
      };
    });

  const subtotal = toNum(invoice.subtotal);
  const vatAmount = toNum(invoice.vat_amount);
  const grand = toNum(invoice.grand_total);

  // Customer TRN stays in party block (left); omit from Bill to to avoid duplicate.
  const billLines: string[] = [];
  const customerBillTo = String(invoice.customer_address ?? '').trim();
  if (customerBillTo) billLines.push(customerBillTo);

  const companyBillLines: string[] = [];
  if (companyInfo.address) companyBillLines.push(companyInfo.address);
  if (companyInfo.phone) companyBillLines.push(`Tel: ${companyInfo.phone}`);
  if (companyInfo.email) companyBillLines.push(`Email: ${companyInfo.email}`);
  if (companyTrn) companyBillLines.push(`TRN: ${companyTrn}`);

  const shipTo = String(inv.shipping_address || inv.delivery_address || '').trim();
  const shipLines = shipTo ? [shipTo] : ['—'];

  const termRows: Array<{ label: string; value: string }> = [];
  if (inv.payment_terms) termRows.push({ label: 'Payment', value: String(inv.payment_terms) });
  const notesPrint = formatInvoiceNotesForPrint(inv.notes);
  if (notesPrint) termRows.push({ label: 'Notes', value: notesPrint });
  if (companyInfo.bankName) termRows.push({ label: 'Bank', value: companyInfo.bankName });
  if (companyInfo.bankAccountNo) termRows.push({ label: 'Account No.', value: companyInfo.bankAccountNo });
  if (companyInfo.bankIban) termRows.push({ label: 'IBAN', value: companyInfo.bankIban });
  if (companyInfo.bankRoutingCode) termRows.push({ label: 'Routing', value: companyInfo.bankRoutingCode });
  if (companyInfo.bankSwiftCode) termRows.push({ label: 'SWIFT', value: companyInfo.bankSwiftCode });
  if (companyInfo.bankAddress) termRows.push({ label: 'Bank Address', value: companyInfo.bankAddress });

  const isAdvance = invoice.invoice_type === 'Advance';
  const docTitle = isAdvance ? 'PROFORMA INVOICE' : 'TAX INVOICE';
  const introText = isAdvance
    ? 'Please find below our proforma invoice for the advance against your order. Kindly arrange the advance payment as per the terms stated.'
    : 'Please find below our tax invoice. Kindly arrange payment as per the terms stated.';

  const doc = buildSimplePrintDocument({
    docType: 'sales_invoice',
    title: docTitle,
    documentNumber: invoice.invoice_number || 'INV',
    partyName: invoice.customer_name || '',
    templateId: 'letter-letterhead',
    letterhead: useLh ? getCompanyLetterhead() : false,
    continuationLabel: `${invoice.invoice_number || 'INV'} | ${invoice.customer_name || ''}`,
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [introText],
    addressGrid: [
      {
        heading: 'Bill to',
        name: invoice.customer_name || '',
        lines: billLines.length ? billLines : companyBillLines.length ? companyBillLines : ['—'],
      },
      { heading: 'Ship to', lines: shipLines },
    ],
    columns,
    lines,
    finals: [
      { id: 'totals', heightPx: 90, keepTogetherWithNext: true },
      { id: 'amountWords', heightPx: 40 },
    ],
    termsHeading: termRows.length ? 'Terms & Notes' : undefined,
    termRows: termRows.length ? termRows : undefined,
    signers: opts?.approvalSigners,
    renderPayload: {
      sectionNodes: deliveryNotesBlock
        ? {
            deliveryNotes: (
              <section className="print-si-delivery-notes">
                <strong>Delivery Notes ({deliveryNoteList.length})</strong>
                <ol>
                  {deliveryNoteList.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ol>
              </section>
            ),
          }
        : undefined,
      finalBlocks: {
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr>
                  <td>Subtotal :</td>
                  <td>{formatPrintAmount(subtotal, currency)}</td>
                </tr>
                <tr>
                  <td>VAT Amount :</td>
                  <td>{formatPrintAmount(vatAmount, currency)}</td>
                </tr>
                <tr className="grand-total">
                  <td>Grand Total :</td>
                  <td>{formatPrintAmount(grand, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
        amountWords: (
          <p className="print-letter-amount-words">
            <span>Total Only:</span>
            <strong>
              ({currency}) {formatAmountInWordsPlain(grand)}
            </strong>
          </p>
        ),
        signatures: (
          <section className="print-dn-signatures print-si-signatures">
            <div className="print-dn-signatures-col">
              <strong>Prepared by</strong>
            </div>
            <div className="print-dn-signatures-col">
              <strong>Received by</strong>
            </div>
          </section>
        ),
      },
    },
  });

  if (deliveryNotesBlock) {
    const gridRows = Math.ceil(deliveryNoteList.length / DELIVERY_NOTE_GRID_COLUMNS);
    doc.firstPageSections.push({ id: 'deliveryNotes', heightPx: 34 + gridRows * 17 });
  }

  // Keep manual signature labels after terms / approval signers.
  doc.finalSections.push({ id: 'signatures', heightPx: 48, keepTogether: true });
  return doc;
}
